export const CATEGORIES = [
  { id: 'studentAgePopulation', label: 'Student-age Population', color: '#4338ca' },
  { id: 'publicSchoolEnrollment', label: 'Public School Enrollment', color: '#0f766e' },
  { id: 'demographics', label: 'Demographics', color: '#b45309' },
  { id: 'schoolQuality', label: 'School Quality', color: '#be123c' },
  { id: 'competition', label: 'Competition', color: '#6d28d9' },
];

export const CATEGORY_COLORS = Object.fromEntries(
  CATEGORIES.map((category) => [category.id, category.color])
);

export const FALLBACK_CATEGORY_COLOR = '#64748b';

export function categoryColor(categoryId) {
  return CATEGORY_COLORS[categoryId] || FALLBACK_CATEGORY_COLOR;
}

export function categoryLabel(categoryId) {
  return CATEGORIES.find((category) => category.id === categoryId)?.label || '';
}

const HIGHER = 'higher';
const LOWER = 'lower';
const PROPORTIONAL = 'proportional';
const HARD = 'hard';
const CAPPED = 'capped';
const STRICT = [false, false, false, false];

const MAGNITUDE_CHANGE = [0.05, 0.01, -0.02, -0.05];
const ENROLLMENT_TREND = [0.05, 0.03, 0.0, -0.05];
const BIRTHS_CHANGE = [0.05, 0.0, -0.02, -0.05];
const HOUSEHOLDS = [0.35, 0.25, 0.2, 0.15];
const CHARTER_SHARE = [0.05, 0.1, 0.15, 0.2];
const STUDENTS_OF_COLOR = [0.6, 0.45, 0.3, 0.15];
const QUALITY_PERFORMANCE = [80, 70, 60, 50];
const QUALITY_GROWTH = [0.55, 0.45, 0.35, 0.25];

const sharedIndicators = {
  enrollmentTrend: {
    id: 'enrollmentTrend',
    label: '5-year Enrollment Change',
    shortLabel: 'Enroll. Δ',
    category: 'publicSchoolEnrollment',
    sourceField: 'historicalEnrollmentChange',
    direction: HIGHER,
    breakpoints: ENROLLMENT_TREND,
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.1,
    format: 'percent',
    cepScoresFive: false,
  },
  householdsBelow50k: {
    id: 'householdsBelow50k',
    label: 'Households under 50k %',
    shortLabel: 'HH < $50k',
    category: 'demographics',
    sourceField: 'pctHouseholdsBelow50k',
    direction: HIGHER,
    breakpoints: HOUSEHOLDS,
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.1,
    format: 'percent',
    cepScoresFive: true,
  },
  charterShare: {
    id: 'charterShare',
    label: 'Charter share',
    shortLabel: 'Charter %',
    category: 'competition',
    sourceField: 'pctPublicStudentsCharter',
    direction: LOWER,
    breakpoints: CHARTER_SHARE,
    inclusive: STRICT,
    floorMode: CAPPED,
    cap: 1,
    defaultWeight: 0.0,
    format: 'percent',
    cepScoresFive: false,
  },
  k12PopChange: {
    id: 'k12PopChange',
    label: '5-year K-12-aged Pop Change',
    shortLabel: 'K-12 pop. Δ',
    category: 'studentAgePopulation',
    sourceField: 'k12Change2020_2026',
    direction: HIGHER,
    breakpoints: MAGNITUDE_CHANGE,
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.05,
    format: 'percent',
    cepScoresFive: false,
  },
  k12PopChangeProjected: {
    id: 'k12PopChangeProjected',
    label: 'Projected 5-year K-12-aged Pop Change',
    shortLabel: 'Proj. K-12 Δ',
    category: 'studentAgePopulation',
    sourceField: 'k12Change2026_2031',
    direction: HIGHER,
    breakpoints: MAGNITUDE_CHANGE,
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.15,
    format: 'percent',
    cepScoresFive: false,
  },
  studentsOfColor: {
    id: 'studentsOfColor',
    label: 'Students of Color %',
    shortLabel: 'SoC %',
    category: 'demographics',
    sourceField: 'pctStudentsOfColor',
    direction: HIGHER,
    breakpoints: STUDENTS_OF_COLOR,
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.1,
    format: 'percent',
    cepScoresFive: true,
  },
  elaPerformance: {
    id: 'elaPerformance',
    label: 'ELA Performance',
    shortLabel: 'ELA Perf.',
    category: 'schoolQuality',
    sourceField: 'elaPerformance',
    direction: HIGHER,
    breakpoints: QUALITY_PERFORMANCE,
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.05,
    format: 'oneDecimal',
    cepScoresFive: false,
  },
  elaGrowth: {
    id: 'elaGrowth',
    label: 'ELA Growth',
    shortLabel: 'ELA Growth',
    category: 'schoolQuality',
    sourceField: 'elaGrowth',
    direction: HIGHER,
    breakpoints: QUALITY_GROWTH,
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.05,
    format: 'percent',
    cepScoresFive: false,
  },
  mathPerformance: {
    id: 'mathPerformance',
    label: 'Math Performance',
    shortLabel: 'Math Perf.',
    category: 'schoolQuality',
    sourceField: 'mathPerformance',
    direction: HIGHER,
    breakpoints: QUALITY_PERFORMANCE,
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.05,
    format: 'oneDecimal',
    cepScoresFive: false,
  },
  mathGrowth: {
    id: 'mathGrowth',
    label: 'Math Growth',
    shortLabel: 'Math Growth',
    category: 'schoolQuality',
    sourceField: 'mathGrowth',
    direction: HIGHER,
    breakpoints: QUALITY_GROWTH,
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.05,
    format: 'percent',
    cepScoresFive: false,
  },
};

export const DISTRICT_INDICATORS = [
  {
    id: 'regionBirthsChange',
    label: '2015-2025 Region Change in Births',
    shortLabel: 'Births Δ',
    category: 'studentAgePopulation',
    sourceField: 'regionBirthsChange2015_2025',
    direction: HIGHER,
    breakpoints: BIRTHS_CHANGE,
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.05,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'regionK12PopChange',
    label: '2020-2026 Region Change in K-12-age pop.',
    shortLabel: 'Region pop. Δ',
    category: 'studentAgePopulation',
    sourceField: 'regionK12Change2020_2026',
    direction: HIGHER,
    breakpoints: MAGNITUDE_CHANGE,
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.0,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'k12Population2026',
    label: '2026 K-12-aged population',
    shortLabel: 'K-12 pop.',
    category: 'studentAgePopulation',
    sourceField: 'k12Population2026',
    direction: HIGHER,
    breakpoints: [20000, 15000, 7000, 3000],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.1,
    format: 'integer',
    cepScoresFive: false,
  },
  sharedIndicators.k12PopChange,
  sharedIndicators.k12PopChangeProjected,
  {
    id: 'enrollment',
    label: '2025-26 K-12 Enrollment Size',
    shortLabel: 'Enrollment',
    category: 'publicSchoolEnrollment',
    sourceField: 'k12Enrollment2025_2026',
    direction: HIGHER,
    breakpoints: [20000, 10000, 5000, 2000],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.15,
    format: 'integer',
    cepScoresFive: false,
  },
  sharedIndicators.enrollmentTrend,
  sharedIndicators.householdsBelow50k,
  sharedIndicators.studentsOfColor,
  sharedIndicators.elaPerformance,
  sharedIndicators.elaGrowth,
  sharedIndicators.mathPerformance,
  sharedIndicators.mathGrowth,
  sharedIndicators.charterShare,
];

export const REGION_INDICATORS = [
  {
    id: 'birthsChange',
    label: '2015-2025 Change in Births',
    shortLabel: 'Births Δ',
    category: 'studentAgePopulation',
    sourceField: 'birthsChange2015_2025',
    direction: HIGHER,
    breakpoints: BIRTHS_CHANGE,
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.05,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'k12Population2026',
    label: '2026 K-12-aged population',
    shortLabel: 'K-12 pop.',
    category: 'studentAgePopulation',
    sourceField: 'k12Population2026',
    direction: HIGHER,
    breakpoints: [120000, 80000, 40000, 20000],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.1,
    format: 'integer',
    cepScoresFive: false,
  },
  sharedIndicators.k12PopChange,
  sharedIndicators.k12PopChangeProjected,
  {
    id: 'enrollment',
    label: '2025-26 K-12 Enrollment Size',
    shortLabel: 'Enrollment',
    category: 'publicSchoolEnrollment',
    sourceField: 'k12Enrollment2025_2026',
    direction: HIGHER,
    breakpoints: [100000, 50000, 25000, 10000],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.15,
    format: 'integer',
    cepScoresFive: false,
  },
  sharedIndicators.enrollmentTrend,
  sharedIndicators.householdsBelow50k,
  sharedIndicators.studentsOfColor,
  sharedIndicators.elaPerformance,
  sharedIndicators.elaGrowth,
  sharedIndicators.mathPerformance,
  sharedIndicators.mathGrowth,
  sharedIndicators.charterShare,
];

// Washington has districts only - no region tier, no births table, and no
// 0-100 ELA/Math growth (WA's growth data is a year behind proficiency and
// lives on a different table - see the Washington City Explorer plan). So
// this list drops Connecticut's regionBirthsChange, regionK12PopChange,
// elaGrowth, and mathGrowth, and gets ELA/Math Performance from
// `proficiency_district.pct_met_standard * 100` (SBAC, All Grades) instead
// of Connecticut's 0-100 performance index. Breakpoints are calibrated to
// the measured distribution of WA districts with enrollment >= 1000 (see
// extract_washington_calculator_data.py), not copied from Connecticut.
// Default weights sum to 1.0; the weight Connecticut spent on the dropped
// indicators (0.15 total) goes +0.05 each to elaPerformance, mathPerformance,
// and k12PopChange here. Unlike Connecticut, ELA/Math score inversely: lower
// proficiency means more need for new options, so it earns more points.
export const WASHINGTON_DISTRICT_INDICATORS = [
  {
    id: 'k12Population2026',
    label: '2026 K-12-aged population',
    shortLabel: 'K-12 pop.',
    category: 'studentAgePopulation',
    sourceField: 'k12Population2026',
    direction: HIGHER,
    breakpoints: [20000, 10000, 4000, 2000],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.1,
    format: 'integer',
    cepScoresFive: false,
  },
  {
    id: 'k12PopChange',
    label: '5-year K-12-aged Pop Change',
    shortLabel: 'K-12 pop. Δ',
    category: 'studentAgePopulation',
    sourceField: 'k12Change2020_2026',
    direction: HIGHER,
    breakpoints: [0.05, 0.01, -0.02, -0.05],
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.1,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'k12PopChangeProjected',
    label: 'Projected 5-year K-12-aged Pop Change',
    shortLabel: 'Proj. K-12 Δ',
    category: 'studentAgePopulation',
    sourceField: 'k12Change2026_2031',
    direction: HIGHER,
    breakpoints: [0.05, 0.01, -0.02, -0.05],
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.15,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'enrollment',
    label: '2025-26 K-12 Enrollment Size',
    shortLabel: 'Enrollment',
    category: 'publicSchoolEnrollment',
    sourceField: 'k12Enrollment2025_2026',
    direction: HIGHER,
    breakpoints: [18000, 9000, 3500, 1500],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.15,
    format: 'integer',
    cepScoresFive: false,
  },
  {
    id: 'enrollmentTrend',
    label: '5-year Enrollment Change',
    shortLabel: 'Enroll. Δ',
    category: 'publicSchoolEnrollment',
    sourceField: 'historicalEnrollmentChange',
    direction: HIGHER,
    breakpoints: ENROLLMENT_TREND,
    inclusive: STRICT,
    floorMode: HARD,
    defaultWeight: 0.1,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'householdsBelow50k',
    label: 'Households under 50k %',
    shortLabel: 'HH < $50k',
    category: 'demographics',
    sourceField: 'pctHouseholdsBelow50k',
    direction: HIGHER,
    breakpoints: [0.35, 0.28, 0.22, 0.15],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.1,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'studentsOfColor',
    label: 'Students of Color %',
    shortLabel: 'SoC %',
    category: 'demographics',
    sourceField: 'pctStudentsOfColor',
    direction: HIGHER,
    breakpoints: [0.6, 0.45, 0.3, 0.2],
    inclusive: STRICT,
    floorMode: PROPORTIONAL,
    defaultWeight: 0.1,
    format: 'percent',
    cepScoresFive: false,
  },
  {
    id: 'elaPerformance',
    label: 'ELA Performance (% Met Standard)',
    shortLabel: 'ELA Perf.',
    category: 'schoolQuality',
    sourceField: 'elaPerformance',
    direction: LOWER,
    breakpoints: [30, 40, 50, 60],
    inclusive: STRICT,
    floorMode: CAPPED,
    cap: 100,
    defaultWeight: 0.1,
    format: 'oneDecimal',
    cepScoresFive: false,
  },
  {
    id: 'mathPerformance',
    label: 'Math Performance (% Met Standard)',
    shortLabel: 'Math Perf.',
    category: 'schoolQuality',
    sourceField: 'mathPerformance',
    direction: LOWER,
    breakpoints: [20, 30, 40, 50],
    inclusive: STRICT,
    floorMode: CAPPED,
    cap: 100,
    defaultWeight: 0.1,
    format: 'oneDecimal',
    cepScoresFive: false,
  },
  {
    id: 'charterShare',
    label: 'Charter share',
    shortLabel: 'Charter %',
    category: 'competition',
    sourceField: 'pctPublicStudentsCharter',
    direction: LOWER,
    breakpoints: CHARTER_SHARE,
    inclusive: STRICT,
    floorMode: CAPPED,
    cap: 1,
    defaultWeight: 0.0,
    format: 'percent',
    cepScoresFive: false,
  },
];

const AB_SHARE_INDICATOR = {
  id: 'abShare',
  label: 'Enrollment in A/B schools %',
  shortLabel: 'A/B schools',
  category: 'schoolQuality',
  sourceField: 'abShare',
  direction: LOWER,
  breakpoints: [10, 30, 55, 85],
  inclusive: STRICT,
  floorMode: CAPPED,
  cap: 100,
  defaultWeight: 0.1,
  format: 'oneDecimal',
  cepScoresFive: false,
};

const ARKANSAS_QUALITY_OVERRIDES = {
  elaPerformance: { breakpoints: [30, 35, 40, 46], defaultWeight: 0.05 },
  mathPerformance: { breakpoints: [33, 40, 46, 53], defaultWeight: 0.05 },
};

// Arkansas: Washington's statewide list plus the enrollment-weighted share of
// students in A/B letter-grade schools (`abShare`, 0-100). School quality keeps
// its 0.2 total: A/B 0.10, ATLAS ELA 0.05, ATLAS Math 0.05. Quality
// breakpoints are roughly the quintiles of AR districts with enrollment >= 1000
// (see extract_washington_calculator_data.py --state AR). Like Washington,
// quality scores inversely: weaker proficiency / fewer students in A/B schools
// means more need for new options, so the bottom quintile earns 5 points.
export const ARKANSAS_DISTRICT_INDICATORS = WASHINGTON_DISTRICT_INDICATORS.flatMap((indicator) => {
  const adjusted = { ...indicator, ...ARKANSAS_QUALITY_OVERRIDES[indicator.id] };
  return indicator.id === 'mathPerformance' ? [adjusted, AB_SHARE_INDICATOR] : [adjusted];
});

// Arkansas counties: same indicator set and sourceFields as
// ARKANSAS_DISTRICT_INDICATORS (every field is built from the same
// metrics_county_polygons / proficiency_district / directory-based abShare
// pipeline, just aggregated by county instead of district - see
// extract_washington_calculator_data.py's county build). Only the
// population-size and enrollment-size breakpoints change: a county
// aggregates several districts, so it runs bigger (Benton/Pulaski County
// are ~55-60k enrollment vs. low thousands for most individual districts).
// Breakpoints below are the roughly-rounded quintiles of AR counties with
// enrollment >= 1,000 (see arkansasCalculatorData.json's `counties`).
const ARKANSAS_COUNTY_SIZE_OVERRIDES = {
  k12Population2026: { breakpoints: [8100, 3400, 2800, 1900] },
  enrollment: { breakpoints: [8100, 3400, 2700, 1700] },
};

export const ARKANSAS_COUNTY_INDICATORS = ARKANSAS_DISTRICT_INDICATORS.map((indicator) => ({
  ...indicator,
  ...ARKANSAS_COUNTY_SIZE_OVERRIDES[indicator.id],
}));

export const LEVELS = {
  district: {
    id: 'district',
    label: 'District',
    entityNoun: 'district',
    entityNounPlural: 'districts',
    indicators: DISTRICT_INDICATORS,
    enrollmentField: 'k12Enrollment2025_2026',
    nameField: 'name',
  },
  region: {
    id: 'region',
    label: 'Region',
    entityNoun: 'region',
    entityNounPlural: 'regions',
    indicators: REGION_INDICATORS,
    enrollmentField: 'k12Enrollment2025_2026',
    nameField: 'name',
  },
};

const shortSchoolYear = (start, end) => `${start}-${end.length === 4 ? end.slice(-2) : end}`;

/**
 * Header for the enrollment column. `enrollmentYear` ("2026-2027", from the
 * extract's meta) wins over the year baked into the field name, which is a
 * fixed schema key (`k12Enrollment2025_2026` holds the latest enrollment).
 */
export function enrollmentColumnLabel(enrollmentField, enrollmentYear) {
  const match =
    String(enrollmentYear || '').match(/^(\d{4})-(\d{2,4})$/) ||
    String(enrollmentField || '').match(/(\d{4})_(\d{2,4})$/);
  if (!match) return 'Enrollment';
  return `${shortSchoolYear(match[1], match[2])} Enrollment`;
}

/** Relabels the enrollment-size indicator with the extract's enrollment year. */
export function withEnrollmentYear(indicators, enrollmentYear) {
  const match = String(enrollmentYear || '').match(/^(\d{4})-(\d{2,4})$/);
  if (!match) return indicators;
  const label = `${shortSchoolYear(match[1], match[2])} K-12 Enrollment Size`;
  return indicators.map((indicator) => (indicator.id === 'enrollment' ? { ...indicator, label } : indicator));
}

export const QUALITY_INDICATOR_IDS = [
  'elaPerformance',
  'elaGrowth',
  'mathPerformance',
  'mathGrowth',
  'abShare',
];

export const KEY_METRIC_COLUMNS = [
  { id: 'enrollmentTrend', label: '5-Year Change in Enrollment' },
  { id: 'studentsOfColor', label: 'Students of Color' },
  { id: 'householdsBelow50k', label: 'Households under $50K' },
  { id: 'qualityAverage', label: 'Quality Metric', composite: 'qualityAverage' },
];

export const ALL_METRIC_EXTRA_COLUMNS = [
  { id: 'k12Population2026', label: '2026 K-12 Population' },
  { id: 'k12PopChange', label: '5-Year Change in K-12 Population' },
  { id: 'k12PopChangeProjected', label: '5-Year Projected Change in K-12 Population' },
  { id: 'regionK12PopChange', label: '5-Year Change in Regional Population' },
  { id: 'regionBirthsChange', altId: 'birthsChange', label: '5-Year Change in Number of Births' },
  { id: 'elaPerformance', label: '2025-2026 ELA Performance Index' },
  { id: 'mathPerformance', label: '2025-2026 Math Performance Index' },
  { id: 'elaGrowth', label: '2025-2026 ELA Growth Rate' },
  { id: 'mathGrowth', label: '2025-2026 Math Growth Rate' },
  { id: 'abShare', label: 'Enrollment in A/B Schools' },
  { id: 'charterShare', label: 'Charter Share' },
];

export const QUALITY_METRIC_NOTE =
  'Average of ELA Performance, ELA Growth, Math Performance, and Math Growth. Growth is scaled to 0–100 so it shares a scale with performance; missing values are left out of the average.';

const QUALITY_NOTE_LABELS = {
  elaPerformance: 'ELA proficiency',
  mathPerformance: 'Math proficiency',
  abShare: 'the share of students in A/B schools',
};

export function qualityMetricNote(indicators) {
  const ids = (indicators || [])
    .map((indicator) => indicator.id)
    .filter((id) => QUALITY_INDICATOR_IDS.includes(id));
  if (ids.includes('elaGrowth') || ids.includes('mathGrowth')) return QUALITY_METRIC_NOTE;
  const labels = ids.map((id) => QUALITY_NOTE_LABELS[id]).filter(Boolean);
  if (!labels.length) return QUALITY_METRIC_NOTE;
  const list =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(', ')}${labels.length > 2 ? ',' : ''} and ${labels[labels.length - 1]}`;
  return `Average of ${list}, each on a 0–100 scale; missing values are left out of the average.`;
}

const ENROLLMENT_COLUMN_ID = 'enrollment';

const resolveMetricColumn = (column, indicators) => {
  if (column.composite) {
    return {
      id: column.id,
      label: column.label,
      format: 'oneDecimal',
      composite: column.composite,
      note: column.note,
    };
  }
  const indicator =
    indicators.find((item) => item.id === column.id) ||
    (column.altId ? indicators.find((item) => item.id === column.altId) : null);
  if (!indicator) return null;
  return {
    id: indicator.id,
    label: column.label,
    format: indicator.format,
    note: column.note,
  };
};

export function qualityAverageValue(entity, indicators) {
  const qualityIndicators = (indicators || []).filter((indicator) =>
    QUALITY_INDICATOR_IDS.includes(indicator.id)
  );
  const scaled = [];
  qualityIndicators.forEach((indicator) => {
    const raw = entity?.[indicator.sourceField];
    if (raw === null || raw === undefined || raw === '') return;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return;
    scaled.push(indicator.format === 'percent' ? numeric * 100 : numeric);
  });
  if (!scaled.length) return null;
  return scaled.reduce((sum, value) => sum + value, 0) / scaled.length;
}

export function keyMetricColumns(indicators) {
  return KEY_METRIC_COLUMNS.map((column) => resolveMetricColumn(column, indicators)).filter(Boolean);
}

export function allMetricColumns(indicators) {
  const specified = [...KEY_METRIC_COLUMNS, ...ALL_METRIC_EXTRA_COLUMNS]
    .map((column) => resolveMetricColumn(column, indicators))
    .filter((column) => column && !column.composite);
  const included = new Set(specified.map((column) => column.id));
  included.add(ENROLLMENT_COLUMN_ID);
  QUALITY_INDICATOR_IDS.forEach((id) => included.add(id));
  const remainder = indicators
    .filter((indicator) => !included.has(indicator.id))
    .map((indicator) => ({
      id: indicator.id,
      label: indicator.label,
      format: indicator.format,
      note: indicator.placeholder ? 'TBD' : undefined,
    }));
  return [...specified, ...remainder];
}

export const DEFAULT_MIN_ENROLLMENT = 1000;

export const WEIGHT_PRESETS = [
  { id: 'default', label: 'Workbook default' },
  { id: 'equal', label: 'Equal weight' },
  { id: 'population', label: 'Population-led' },
  { id: 'enrollment', label: 'Enrollment-led' },
];

export function defaultWeights(indicators) {
  return Object.fromEntries(indicators.map((indicator) => [indicator.id, indicator.defaultWeight]));
}

export function defaultBreakpoints(indicators) {
  return Object.fromEntries(
    indicators.map((indicator) => [indicator.id, [...indicator.breakpoints]])
  );
}

export function presetWeights(presetId, indicators) {
  if (presetId === 'equal') {
    const weight = 1 / indicators.length;
    return Object.fromEntries(indicators.map((indicator) => [indicator.id, weight]));
  }

  if (presetId === 'population' || presetId === 'enrollment') {
    const leadCategory =
      presetId === 'population' ? 'studentAgePopulation' : 'publicSchoolEnrollment';
    const leadShare = presetId === 'population' ? 0.6 : 0.5;
    const lead = indicators.filter((indicator) => indicator.category === leadCategory);
    const rest = indicators.filter((indicator) => indicator.category !== leadCategory);
    const leadWeight = lead.length ? leadShare / lead.length : 0;
    const restWeight = rest.length ? (1 - leadShare) / rest.length : 0;
    return Object.fromEntries([
      ...lead.map((indicator) => [indicator.id, leadWeight]),
      ...rest.map((indicator) => [indicator.id, restWeight]),
    ]);
  }

  return defaultWeights(indicators);
}

export function weightSum(weights) {
  return Object.values(weights).reduce((sum, value) => sum + (Number(value) || 0), 0);
}

export function normalizeWeights(weights) {
  const sum = weightSum(weights);
  if (sum <= 0) return { ...weights };
  return Object.fromEntries(
    Object.entries(weights).map(([id, value]) => [id, (Number(value) || 0) / sum])
  );
}
